/** Making a credential: a name for it, and a list of what it may send.
 *
 * Scopes are per data type, which is the point of having them — an
 * installation that only surveys circuits should hold a token that cannot
 * post a lap, so that losing it loses nothing else. The boxes are therefore
 * the decision this card is really asking about, and each one says in a line
 * what that scope permits rather than leaving `sessions:write` to explain
 * itself.
 *
 * Only the types the server is currently accepting are offered, read from
 * `GET /v1/capabilities` — the same document the logger reads before it
 * renders its own toggles. A token scoped to a disabled type would be issued
 * and then refused by every endpoint it was made for, so the server declines
 * to make one; offering the box anyway would turn that rule into an error
 * message. A type that is off is named in the note instead, because "where is
 * live?" is the question that follows.
 *
 * If capabilities cannot be read at all the boxes are all offered and the
 * note says so. Guessing wrong here costs one refused request with a reason
 * on it; guessing the other way would hide a type that is switched on.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardTitle, Checkbox, ErrorNote, Field, Kicker } from '@/components/ui';
import type { DataType, Scope } from '@/lib/types';

export interface CreateTokenCardProps {
  /** The types `/v1/capabilities` lists, or null when it could not be read. */
  enabledTypes: readonly DataType[] | null;
  /** Creates the token. Throwing leaves the form as it was, with the reason. */
  onCreate: (label: string, scopes: Scope[]) => Promise<void>;
  /** True when the account is already at the server's live-token ceiling. */
  atLimit?: boolean;
  limit?: number;
}

interface ScopeOption {
  type: DataType;
  scope: Scope;
  description: string;
}

/** One write scope per data type, in the order the design lists them. */
const OPTIONS: readonly ScopeOption[] = [
  {
    type: 'tracks',
    scope: 'tracks:write',
    description: 'community survey bundles, filed as pull requests',
  },
  {
    type: 'sessions',
    scope: 'sessions:write',
    description: 'your own laps and racing lines, private by default',
  },
  {
    type: 'live',
    scope: 'live:write',
    description: 'a 4 Hz position stream for spectators',
  },
];

export function CreateTokenCard({ enabledTypes, onCreate, atLimit, limit }: CreateTokenCardProps) {
  const [label, setLabel] = useState('');
  const [chosen, setChosen] = useState<ReadonlySet<Scope>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const offered = useMemo(
    () =>
      enabledTypes === null
        ? OPTIONS
        : OPTIONS.filter((option) => enabledTypes.includes(option.type)),
    [enabledTypes],
  );

  const withheld = useMemo(
    () =>
      enabledTypes === null
        ? []
        : OPTIONS.filter((option) => !enabledTypes.includes(option.type)).map(
            (option) => option.type,
          ),
    [enabledTypes],
  );

  // What an installation normally sends: the community data and the driver's
  // own laps. Live is a decision, not a default — it is a stream anybody with
  // the URL may watch.
  useEffect(() => {
    setChosen((held) => {
      if (held.size > 0) return held;
      const next = new Set<Scope>();
      for (const option of offered) {
        if (option.type !== 'live') next.add(option.scope);
      }
      return next;
    });
  }, [offered]);

  const scopes = offered.filter((option) => chosen.has(option.scope)).map((option) => option.scope);
  const ready = label.trim().length > 0 && scopes.length > 0 && !busy && !atLimit;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onCreate(label.trim(), scopes);
      setLabel('');
    } catch (thrown) {
      setError(thrown);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" label="Connect an installation" style={{ gap: 14 }}>
      <div>
        <Kicker>new token</Kicker>
        <CardTitle as="h2" className="mt-1">
          Connect an installation
        </CardTitle>
      </div>

      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void submit();
        }}
      >
        <Field label="Label">
          {({ id, describedBy }) => (
            <input
              id={id}
              className="input"
              value={label}
              maxLength={60}
              disabled={busy}
              placeholder="e.g. Living room PS5"
              aria-describedby={describedBy}
              onChange={(event) => setLabel(event.currentTarget.value)}
            />
          )}
        </Field>

        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend
            style={{
              fontSize: 12,
              color: 'color-mix(in srgb, var(--color-text) 70%, transparent)',
              marginBottom: 4,
            }}
          >
            Scopes — what this token may send
          </legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {offered.length === 0 ? (
              <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
                This server is not accepting any data type at the moment, so there is nothing a
                token could be scoped to.
              </span>
            ) : null}
            {offered.map((option) => (
              <Checkbox
                key={option.scope}
                checked={chosen.has(option.scope)}
                disabled={busy}
                label={<code style={{ fontSize: 12.5 }}>{option.scope}</code>}
                description={option.description}
                onChange={(next) =>
                  setChosen((held) => {
                    const copy = new Set(held);
                    if (next) copy.add(option.scope);
                    else copy.delete(option.scope);
                    return copy;
                  })
                }
              />
            ))}
          </div>
        </fieldset>

        {error ? <ErrorNote error={error} title="could not make that token" compact /> : null}

        <button type="submit" className="btn btn-primary btn-block" disabled={!ready}>
          {busy ? 'Creating…' : 'Create token'}
        </button>
      </form>

      <p style={{ fontSize: 11, color: 'var(--color-neutral-600)', margin: 0, textWrap: 'pretty' }}>
        {atLimit ? (
          <>
            This account already holds {limit ?? 'the maximum number of'} live tokens. Revoke one
            before making another.{' '}
          </>
        ) : null}
        Only types the server currently accepts are offered.
        {enabledTypes === null ? (
          <> This server&rsquo;s capabilities could not be read, so all three are shown.</>
        ) : withheld.length > 0 ? (
          <>
            {' '}
            {withheld.map((type, index) => (
              <span key={type}>
                {index === 0 ? '' : index === withheld.length - 1 ? ' and ' : ', '}
                <code>{type}</code>
              </span>
            ))}{' '}
            {withheld.length > 1 ? 'are' : 'is'} off on this server.
          </>
        ) : null}
      </p>
    </Card>
  );
}

export default CreateTokenCard;
